import React, { useState, useEffect } from 'react';
import { X, Plus, Edit3, Trash2, Save, Type, Star, ArrowUp, ArrowDown, ToggleLeft, ToggleRight, ExternalLink } from 'lucide-react';
import { 
  getAllFeedbackFormFields, 
  createFeedbackFormField, 
  updateFeedbackFormField, 
  deleteFeedbackFormField, 
  reorderFeedbackFormFields,
  FeedbackFormField,
  CreateFieldRequest,
  UpdateFieldRequest
} from '../lib/feedbackForm';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

interface CustomizeFeedbackFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FieldEditState {
  id: string | null;
  field_label: string;
  field_type: 'rating' | 'text';
  is_required: boolean;
}

interface BusinessGoogleReviewSettings {
  google_reviews_enabled: boolean;
  google_review_condition: number;
  google_review_url: string;
}

const defaultFieldState: FieldEditState = {
  id: null,
  field_label: '',
  field_type: 'rating',
  is_required: false
};

export default function CustomizeFeedbackFormModal({ isOpen, onClose }: CustomizeFeedbackFormModalProps) {
  const { selectedBusiness } = useBusiness();
  const [fields, setFields] = useState<FeedbackFormField[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingField, setEditingField] = useState<FieldEditState>(defaultFieldState);
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Google Reviews settings
  const [googleReviewsEnabled, setGoogleReviewsEnabled] = useState(false);
  const [googleReviewCondition, setGoogleReviewCondition] = useState('5'); // Default to 5 stars
  const [googleReviewUrl, setGoogleReviewUrl] = useState('');

  // Load existing fields
  const loadFields = async () => {
    if (!selectedBusiness?.id) return;
    
    setLoading(true);
    try {
      const formFields = await getAllFeedbackFormFields(selectedBusiness.id);
      setFields(formFields);
    } catch (error) {
      console.error('Error loading feedback form fields:', error);
      toast.error('Failed to load feedback form fields');
    } finally {
      setLoading(false);
    }
  };

  // Load Google review settings
  const loadGoogleReviewSettings = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      const { data, error } = await supabase
        .from('businesses')
        .select('google_reviews_enabled, google_review_condition, google_review_url')
        .eq('id', selectedBusiness.id)
        .single();

      if (error) {
        console.error('Error loading Google review settings:', error);
        return;
      }

      if (data) {
        setGoogleReviewsEnabled(data.google_reviews_enabled || false);
        setGoogleReviewCondition(String(data.google_review_condition || 5));
        setGoogleReviewUrl(data.google_review_url || '');
      }
    } catch (error) {
      console.error('Error loading Google review settings:', error);
    }
  };

  // Save Google review settings
  const saveGoogleReviewSettings = async () => {
    if (!selectedBusiness?.id || !supabase) return false;
    
    try {
      const { error } = await supabase
        .from('businesses')
        .update({
          google_reviews_enabled: googleReviewsEnabled,
          google_review_condition: parseInt(googleReviewCondition),
          google_review_url: googleReviewUrl.trim()
        })
        .eq('id', selectedBusiness.id);

      if (error) {
        console.error('Error saving Google review settings:', error);
        toast.error('Failed to save Google review settings');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error saving Google review settings:', error);
      toast.error('Failed to save Google review settings');
      return false;
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadFields();
      loadGoogleReviewSettings();
      setShowAddForm(false);
      setEditingField(defaultFieldState);
    }
  }, [isOpen, selectedBusiness?.id]);

  // Handle field creation
  const handleCreateField = async () => {
    if (!selectedBusiness?.id || !editingField.field_label.trim()) return;

    setSaving(true);
    try {
      const fieldKey = editingField.field_label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      
      const newFieldRequest: CreateFieldRequest = {
        field_key: fieldKey,
        field_label: editingField.field_label.trim(),
        field_type: editingField.field_type,
        is_required: editingField.is_required,
        display_order: fields.length + 1
      };

      const newField = await createFeedbackFormField(selectedBusiness.id, newFieldRequest);
      
      if (newField) {
        toast.success('Question added successfully');
        setEditingField(defaultFieldState);
        setShowAddForm(false);
        await loadFields();
      } else {
        toast.error('Failed to add question');
      }
    } catch (error) {
      console.error('Error creating field:', error);
      toast.error('Failed to add question');
    } finally {
      setSaving(false);
    }
  };

  // Handle field update
  const handleUpdateField = async () => {
    if (!selectedBusiness?.id || !editingField.id || !editingField.field_label.trim()) return;

    // Check if changing from rating to text would leave no rating fields
    const currentField = fields.find(f => f.id === editingField.id);
    if (currentField?.field_type === 'rating' && editingField.field_type === 'text') {
      const ratingFields = fields.filter(f => f.field_type === 'rating');
      if (ratingFields.length === 1) {
        toast.error('You must have at least one star rating question in your feedback form');
        return;
      }
    }

    setSaving(true);
    try {
      const updateRequest: UpdateFieldRequest = {
        field_label: editingField.field_label.trim(),
        field_type: editingField.field_type,
        is_required: editingField.is_required
      };

      const success = await updateFeedbackFormField(editingField.id, updateRequest);
      
      if (success) {
        toast.success('Question updated successfully');
        setEditingField(defaultFieldState);
        await loadFields();
      } else {
        toast.error('Failed to update question');
      }
    } catch (error) {
      console.error('Error updating field:', error);
      toast.error('Failed to update question');
    } finally {
      setSaving(false);
    }
  };

  // Handle field deletion
  const handleDeleteField = async (fieldId: string) => {
    if (!selectedBusiness?.id) return;

    // Check if this is the last rating field
    const ratingFields = fields.filter(f => f.field_type === 'rating');
    const fieldToDelete = fields.find(f => f.id === fieldId);
    
    if (fieldToDelete?.field_type === 'rating' && ratingFields.length === 1) {
      toast.error('You must have at least one star rating question in your feedback form');
      return;
    }

    if (!confirm('Are you sure you want to delete this question? This action cannot be undone.')) return;

    setSaving(true);
    try {
      const success = await deleteFeedbackFormField(fieldId);
      
      if (success) {
        toast.success('Question deleted successfully');
        await loadFields();
      } else {
        toast.error('Failed to delete question');
      }
    } catch (error) {
      console.error('Error deleting field:', error);
      toast.error('Failed to delete question');
    } finally {
      setSaving(false);
    }
  };

  // Handle field reordering
  const moveField = async (fieldId: string, direction: 'up' | 'down') => {
    const currentIndex = fields.findIndex(f => f.id === fieldId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= fields.length) return;

    const newFields = [...fields];
    const [movedField] = newFields.splice(currentIndex, 1);
    newFields.splice(newIndex, 0, movedField);

    if (!selectedBusiness?.id) return;

    const fieldOrders = newFields.map((field, index) => ({
      id: field.id,
      display_order: index + 1
    }));

    try {
      const success = await reorderFeedbackFormFields(selectedBusiness.id, fieldOrders);
      
      if (success) {
        setFields(newFields);
      } else {
        toast.error('Failed to reorder questions');
      }
    } catch (error) {
      console.error('Error reordering fields:', error);
      toast.error('Failed to reorder questions');
    }
  };

  const startEditField = (field: FeedbackFormField) => {
    setEditingField({
      id: field.id,
      field_label: field.field_label,
      field_type: field.field_type,
      is_required: field.is_required
    });
    setShowAddForm(false);
  };

  const startAddField = () => {
    setEditingField(defaultFieldState);
    setShowAddForm(true);
  };

  const cancelEdit = () => {
    setEditingField(defaultFieldState);
    setShowAddForm(false);
  };

  const handleDone = async () => {
    // Save Google review settings before closing
    if (googleReviewsEnabled && googleReviewUrl.trim()) {
      const saved = await saveGoogleReviewSettings();
      if (saved) {
        toast.success('Google review settings saved successfully');
      }
    } else if (googleReviewsEnabled && !googleReviewUrl.trim()) {
      toast.error('Please enter a Google review URL before enabling Google reviews');
      return; // Don't close modal if URL is missing
    } else {
      // Save disabled state
      await saveGoogleReviewSettings();
    }
    
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Customize Your Feedback Form</h2>
            <p className="text-gray-600 mt-1">Create questions that matter to your business</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-160px)]">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading your feedback form...</span>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto">
              {/* Questions List */}
              {fields.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Your Questions</h3>
                    <span className="text-sm text-gray-500">{fields.length} question{fields.length !== 1 ? 's' : ''}</span>
                  </div>
                  
                  <div className="space-y-3">
                    {fields.map((field, index) => (
                      <div key={field.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded">
                                Q{index + 1}
                              </span>
                              <span className="font-medium text-gray-900">{field.field_label}</span>
                              {field.is_required && (
                                <span className="text-red-500 text-xs bg-red-50 px-2 py-1 rounded">Required</span>
                              )}
                            </div>
                            <div className="flex items-center space-x-2 text-sm text-gray-500">
                              {field.field_type === 'rating' ? (
                                <>
                                  <Star className="h-4 w-4" />
                                  <span>Star Rating (1-5)</span>
                                </>
                              ) : (
                                <>
                                  <Type className="h-4 w-4" />
                                  <span>Text Response</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {/* Move buttons */}
                            <div className="flex flex-col space-y-1">
                              <button
                                onClick={() => moveField(field.id, 'up')}
                                disabled={index === 0}
                                className={`p-1 rounded transition-colors ${
                                  index === 0 
                                    ? 'text-gray-300 cursor-not-allowed' 
                                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                }`}
                              >
                                <ArrowUp className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => moveField(field.id, 'down')}
                                disabled={index === fields.length - 1}
                                className={`p-1 rounded transition-colors ${
                                  index === fields.length - 1 
                                    ? 'text-gray-300 cursor-not-allowed' 
                                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                }`}
                              >
                                <ArrowDown className="h-3 w-3" />
                              </button>
                            </div>
                            {/* Edit button */}
                            <button
                              onClick={() => startEditField(field)}
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                            {/* Delete button */}
                            <button
                              onClick={() => handleDeleteField(field.id)}
                              disabled={field.field_type === 'rating' && fields.filter(f => f.field_type === 'rating').length === 1}
                              className={`p-2 rounded transition-colors ${
                                field.field_type === 'rating' && fields.filter(f => f.field_type === 'rating').length === 1
                                  ? 'text-gray-300 cursor-not-allowed'
                                  : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                              }`}
                              title={
                                field.field_type === 'rating' && fields.filter(f => f.field_type === 'rating').length === 1
                                  ? 'Cannot delete the last star rating question'
                                  : 'Delete question'
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add/Edit Form */}
              {(showAddForm || editingField.id) && (
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200 mb-8">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    {editingField.id ? 'Edit Question' : 'Add New Question'}
                  </h4>
                  
                  <div className="space-y-4">
                    {/* Question Text */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        What do you want to ask your customers?
                      </label>
                      <input
                        type="text"
                        value={editingField.field_label}
                        onChange={(e) => setEditingField(prev => ({ ...prev, field_label: e.target.value }))}
                        placeholder="e.g., How would you rate the quality of our service?"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    {/* Response Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        How should customers respond?
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <label className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                          editingField.field_type === 'rating' 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}>
                          <input
                            type="radio"
                            name="field_type"
                            value="rating"
                            checked={editingField.field_type === 'rating'}
                            onChange={(e) => setEditingField(prev => ({ ...prev, field_type: e.target.value as 'rating' | 'text' }))}
                            className="sr-only"
                          />
                          <div className="flex flex-col items-center text-center">
                            <Star className="h-6 w-6 mb-2 text-yellow-500" />
                            <span className="font-medium text-gray-900">Star Rating</span>
                            <span className="text-xs text-gray-500 mt-1">1 to 5 stars</span>
                          </div>
                        </label>
                        
                        <label className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                          editingField.field_type === 'text' 
                            ? 'border-blue-500 bg-blue-50' 
                            : (() => {
                                if (editingField.id) {
                                  const currentField = fields.find(f => f.id === editingField.id);
                                  const isLastRatingField = currentField?.field_type === 'rating' && fields.filter(f => f.field_type === 'rating').length === 1;
                                  return isLastRatingField 
                                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                                    : 'border-gray-200 hover:border-gray-300';
                                }
                                return 'border-gray-200 hover:border-gray-300';
                              })()
                        }`}>
                          <input
                            type="radio"
                            name="field_type"
                            value="text"
                            checked={editingField.field_type === 'text'}
                            onChange={(e) => setEditingField(prev => ({ ...prev, field_type: e.target.value as 'rating' | 'text' }))}
                            disabled={(() => {
                              if (editingField.id) {
                                const currentField = fields.find(f => f.id === editingField.id);
                                return currentField?.field_type === 'rating' && fields.filter(f => f.field_type === 'rating').length === 1;
                              }
                              return false;
                            })()}
                            className="sr-only"
                          />
                          <div className="flex flex-col items-center text-center">
                            <Type className="h-6 w-6 mb-2 text-blue-500" />
                            <span className="font-medium text-gray-900">Text Response</span>
                            <span className="text-xs text-gray-500 mt-1">Written feedback</span>
                          </div>
                        </label>
                      </div>
                      {(() => {
                        if (editingField.id) {
                          const currentField = fields.find(f => f.id === editingField.id);
                          const isLastRatingField = currentField?.field_type === 'rating' && fields.filter(f => f.field_type === 'rating').length === 1;
                          return isLastRatingField ? (
                            <p className="text-xs text-amber-600 mt-2 flex items-center">
                              <span className="mr-1">⚠️</span>
                              You must have at least one star rating question in your feedback form
                            </p>
                          ) : null;
                        }
                        return null;
                      })()}
                    </div>

                    {/* Required Toggle */}
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editingField.is_required}
                          onChange={(e) => setEditingField(prev => ({ ...prev, is_required: e.target.checked }))}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-700">
                          Make this question required
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1 ml-7">
                        Customers must answer this question before submitting
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={cancelEdit}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={editingField.id ? handleUpdateField : handleCreateField}
                        disabled={saving || !editingField.field_label.trim()}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {saving ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            {editingField.id ? 'Update Question' : 'Add Question'}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Google Reviews Integration Section */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200 mb-8">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <ExternalLink className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">Google Reviews Integration</h4>
                      <p className="text-sm text-gray-600">Automatically request Google reviews from satisfied customers</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setGoogleReviewsEnabled(!googleReviewsEnabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                      googleReviewsEnabled ? 'bg-green-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        googleReviewsEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center space-x-2 mb-4">
                  <span className={`text-sm font-medium ${googleReviewsEnabled ? 'text-green-800' : 'text-gray-600'}`}>
                    {googleReviewsEnabled ? 'Google Reviews Enabled' : 'Enable Google Reviews'}
                  </span>
                  {googleReviewsEnabled && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Active
                    </span>
                  )}
                </div>

                {/* Expanded Google Reviews Settings */}
                {googleReviewsEnabled && (
                  <div className="space-y-6 mt-6 p-4 bg-white rounded-lg border border-green-100">
                    <div>
                      <h5 className="text-md font-semibold text-gray-900 mb-3">Review Request Conditions</h5>
                      <p className="text-sm text-gray-600 mb-4">
                        Set up automatic Google review requests based on customer feedback
                      </p>
                    </div>

                    {/* Condition Settings */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-4">
                        <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded">IF</span>
                        <span className="text-sm text-gray-700">Customer gives a rating of</span>
                      </div>
                      
                      <div className="flex items-center space-x-4 mb-4">
                        <select
                          value={googleReviewCondition}
                          onChange={(e) => setGoogleReviewCondition(e.target.value)}
                          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          <option value="5">5 stars (Excellent)</option>
                          <option value="4">4 stars or higher (Good+)</option>
                          <option value="3">3 stars or higher (Average+)</option>
                        </select>
                        <span className="text-sm text-gray-600">or higher</span>
                      </div>

                      <div className="flex items-center space-x-2 mb-4">
                        <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded">THEN</span>
                        <span className="text-sm text-gray-700">Show Google review request</span>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-start space-x-2">
                          <div className="mt-0.5">
                            <div className="h-2 w-2 bg-blue-400 rounded-full"></div>
                          </div>
                          <div className="text-sm text-blue-800">
                            <p className="font-medium">What happens:</p>
                            <ul className="mt-1 space-y-1 text-xs">
                              <li>• Customer sees a "Leave us a Google review" button</li>
                              <li>• Clicking opens your Google Business listing</li>
                              <li>• Customer can easily leave a public review</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Google Review URL Configuration */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Google Review URL <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={googleReviewUrl}
                        onChange={(e) => setGoogleReviewUrl(e.target.value)}
                        placeholder="e.g., https://g.page/r/YourBusinessName/review"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        Enter the direct link to your Google review page. Customers will click this link to leave a review.
                        <a href="https://support.google.com/business/answer/3474050" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 ml-1">
                          How to get your Google review link →
                        </a>
                      </p>
                    </div>

                    {/* Preview Section */}
                    <div className="border-t border-gray-200 pt-4">
                      <h6 className="text-sm font-medium text-gray-900 mb-3">Customer Experience Preview</h6>
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <div className="flex space-x-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star 
                                  key={star} 
                                  className={`h-4 w-4 ${star <= parseInt(googleReviewCondition) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} 
                                />
                              ))}
                            </div>
                            <span className="text-sm font-medium text-gray-900">
                              {googleReviewCondition} star{parseInt(googleReviewCondition) !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <p className="text-sm font-medium text-green-800 mb-2">🎉 Thanks for the great feedback!</p>
                          <p className="text-sm text-green-700 mb-3">
                            Would you mind sharing your experience on Google to help other customers find us?
                          </p>
                          <button className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors">
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Leave Google Review
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Add Question Button or Empty State */}
              {!showAddForm && !editingField.id && (
                <div className="text-center">
                  {fields.length === 0 ? (
                    <div className="py-12">
                      <div className="text-gray-400 mb-4">
                        <Star className="h-12 w-12 mx-auto" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No questions yet</h3>
                      <p className="text-gray-500 mb-6">Start by adding your first feedback question</p>
                      <button
                        onClick={startAddField}
                        className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                      >
                        <Plus className="h-5 w-5 mr-2" />
                        Add Your First Question
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={startAddField}
                      className="inline-flex items-center px-6 py-3 border border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 transition-colors"
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      Add Another Question
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {fields.length === 0 ? (
              'Start by adding your first question'
            ) : (
              <>
                {`${fields.length} question${fields.length !== 1 ? 's' : ''} in your feedback form`}
                {fields.filter(f => f.field_type === 'rating').length === 0 && (
                  <span className="text-amber-600 ml-2">⚠️ Missing required star rating question</span>
                )}
              </>
            )}
          </div>
          <button
            onClick={handleDone}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
} 
 